/**
 * Corporate Governance Page (English)
 * /en/corporate-governance/
 */
import React, { useState } from 'react'
import { graphql, PageProps } from 'gatsby'
import styled from '@emotion/styled'
import Layout from '../../components/Layout'
import HeroBanner from '../../components/HeroBanner'
import SEO from '../../components/SEO'
import PageTitle from '../../components/PageTitle'
import CommitteeGrid from '../../components/CommitteeGrid'
import CommitteeSection from '../../components/CommitteeSection'
import RecordList from '../../components/RecordList'
import RecordItem from '../../components/RecordItem'
import Pagination from '../../components/Pagination'
import { CommitteeConnection, IRDocumentConnection } from '../../types'
import corporateGovernanceRecordsData from '../../data/en-corporate-governance-records.json'
import corporateGovernanceMembersData from '../../data/en-corporate-governance-members.json'

const GovernanceRecordsSection = styled.div`
  margin: 2rem auto 5rem auto;
  padding: 2rem;
  min-height: 20vh;
`

const GovernanceEmptyRecord = styled.span`
  padding: 1rem 0.3rem;
  display: flex;
  justify-content: space-between;
  color: #000;
`

export const query = graphql`
  query CorporateGovernanceEnQuery {
    records: allSanityRecord(
      filter: { category: { name: { en: { eq: "Corporate Governance" } } } }
      sort: { fields: date, order: DESC }
    ) {
      totalCount
      edges {
        node {
          id
          name {
            zhHant
            en
          }
          date
          file {
            zhHant {
              asset {
                url
              }
            }
            en {
              asset {
                url
              }
            }
          }
        }
      }
    }
    members: allSanityMember {
      totalCount
      edges {
        node {
          id
          title {
            zhHant
            en
          }
          member {
            zhHant
            en
          }
        }
      }
    }
  }
`

interface CorporateGovernanceEnPageProps extends PageProps {
  data?: {
    records: IRDocumentConnection
    members: CommitteeConnection
  }
}

const PER_PAGE = 10
const PAGE_TITLE = 'Corporate Governance'
const LANG_KEY = 'en' as const
const LANG_FIELD = 'en' as const
const COMMITTEE_ORDER = [
  'NOMINATION COMMITTEE',
  'AUDIT COMMITTEE',
  'AUTHORISED REPRESENTATIVES',
  'COMPANY SECRETARY',
  'REMUNERATION COMMITTEE',
]

const localRecords = corporateGovernanceRecordsData as IRDocumentConnection
const localMembers = corporateGovernanceMembersData as CommitteeConnection

const sortCommitteeEdges = (edges: CommitteeConnection['edges']) =>
  [...edges].sort((a, b) => {
    const aIdx = COMMITTEE_ORDER.indexOf(a.node.title.en)
    const bIdx = COMMITTEE_ORDER.indexOf(b.node.title.en)

    return (aIdx === -1 ? COMMITTEE_ORDER.length : aIdx) - (bIdx === -1 ? COMMITTEE_ORDER.length : bIdx)
  })

const CorporateGovernanceEnPage: React.FC<CorporateGovernanceEnPageProps> = ({ data, location }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const records = data?.records?.totalCount ? data.records : localRecords
  const members = data?.members?.totalCount ? data.members : localMembers
  const { edges: recordEdges, totalCount } = records
  const memberEdges = sortCommitteeEdges(members.edges)

  const endIdx = PER_PAGE * currentPage
  const startIdx = endIdx - PER_PAGE
  const isFirst = currentPage === 1
  const isLast = totalCount === 0 || currentPage === Math.ceil(totalCount / PER_PAGE)
  const currentItems = recordEdges.slice(startIdx, endIdx)

  return (
    <Layout langKey={LANG_KEY} fluid={true} currentPath={location.pathname}>
      <SEO title={PAGE_TITLE} lang="en" />
      <HeroBanner />

      <CommitteeGrid>
        {memberEdges.map(({ node }, idx) => (
          <CommitteeSection
            key={node.id || idx}
            name={node.title[LANG_FIELD]}
            members={node.member}
            langKey={LANG_FIELD}
          />
        ))}
      </CommitteeGrid>

      <GovernanceRecordsSection>
        <PageTitle title={PAGE_TITLE} />
        <RecordList>
          {currentItems.length > 0 ? (
            currentItems.map(({ node }, idx) =>
              node.name[LANG_FIELD] ? (
                <RecordItem
                  key={node.id || `record-${idx}`}
                  {...node}
                  langKey={LANG_FIELD}
                  isLast={totalCount === idx + 1}
                />
              ) : null
            )
          ) : (
            <GovernanceEmptyRecord>No records found</GovernanceEmptyRecord>
          )}
        </RecordList>
        <Pagination
          langKey={LANG_KEY}
          perPage={PER_PAGE}
          isFirst={isFirst}
          isLast={isLast}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          noRecord={totalCount === 0}
          totalCount={totalCount}
        />
      </GovernanceRecordsSection>
    </Layout>
  )
}

export default CorporateGovernanceEnPage
